<template>
  <div class="card h-100 tags-trends-widget">
    <div class="card-header">
      <h4 class="card-title">
        {{ widgetTitle }}
      </h4>
    </div>
    <div class="card-body">
      <div class="row default-actions-stats mb-5">
        <div class="col">
          <p class="text-muted fs-5 mb-1">
            {{ $t('widgetActionsSummary.labels.totalActions') }}
          </p>
          <p class="fs-4">
            <span v-if="totalSelectedActions">
              {{ totalSelectedActions }}
            </span>
            <span v-else>
              <i class="bi bi-info-circle-fill fs-3 text-info me-3"></i>
              {{ $t('widgetGroupActions.noActionsSelectedInCategory') }}
            </span>
          </p>
        </div>
        <div class="col">
          <p class="text-muted fs-5 mb-1">
            {{ $t('widgetActionsSummary.labels.peopleThatSelectedAnAction') }}
          </p>
          <p class="fs-4">
            {{ peopleSelectedAtLeastOneAction }}
            ({{ percentageOfSelectedAction(peopleSelectedAtLeastOneAction) }})
          </p>
        </div>
      </div>
      <div class="row summary-actions-stats">
        <div
          v-if="individualActionsWithLabels.actionsWithLabels"
          class="col col-6"
        >
          <div
            v-for="summaryName in Object.keys(
              individualActionsWithLabels.actionsWithLabels
            )"
            :key="summaryName"
            class="mb-8"
          >
            <p class="text-muted fs-5 mb-1">
              {{
                individualActionsWithLabels.actionsWithLabels[summaryName].label
              }}
            </p>
            <span class="fs-4">
              {{
                individualActionsWithLabels.actionsWithLabels[summaryName]
                  .summaryActionSelectionCount
              }}
            </span>
          </div>
        </div>
        <div
          v-if="individualActionsWithLabels.actionStatsWithLabels"
          class="col col-6"
        >
          <div
            v-for="summaryName in Object.keys(
              individualActionsWithLabels.actionStatsWithLabels
            )"
            :key="summaryName"
            class="mb-8"
          >
            <p class="text-muted fs-5 mb-1">
              {{
                individualActionsWithLabels.actionStatsWithLabels[summaryName]
                  .label
              }}
            </p>
            <span class="fs-4">
              {{
                individualActionsWithLabels.actionStatsWithLabels[summaryName]
                  .numberOfPeopleThatSelectedAnAction
              }}
              {{
                individualActionsWithLabels.actionStatsWithLabels[summaryName]
                  .percentageOfPeopleThatSelectedAnAction
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import commonWidgetProps from '@/components/mixins/common-widget-props'
import { requestTypes } from '@/constants/request-types'
import TagTrendsDetails from '@/components/shared/TagTrendsDetails.vue'
import { errorTypes } from '@/constants/error-types'

export const actionSummaryInfoWidgetInformation = ({ colData }) => {
  const doneOnly = !!colData.doneOnly
  const summaryActions = Object.keys(colData.summaryActions || {}).reduce(
    (actions, summaryName) => {
      actions[summaryName] = {
        actions: colData.summaryActions[summaryName].action_names
      }

      return actions
    },
    {}
  )
  return {
    requests: {
      [requestTypes.TAGS]: {
        filter: {
          participation: {}
        }
      },
      [requestTypes.SURVEY_DETAILS]: {},

      [requestTypes.ACTIONS]: {
        params: {
          summaryActions,
          doneOnly
        }
      }
    }
  }
}

export const actionSummaryInfoWidgetSkeleton = () => {
  return {
    header: {
      height: 2
    },
    body: [
      {
        cols: [{ height: 2 }]
      },
      {
        cols: [{ height: 10 }]
      }
    ]
  }
}

const ActionSummaryInfoWidget = {
  name: 'ActionSummaryInfoWidget',
  components: {
    TagTrendsDetails
  },
  mixins: [commonWidgetProps],
  props: {
    actions: {
      type: Object,
      default: () => {}
    },
    summaryActions: {
      type: Object,
      default: () => {}
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    id: {
      type: String,
      default: ''
    },
    tags: {
      type: Object,
      default: () => {}
    },
    trendTags: {
      type: Object,
      default: () => {}
    },
    chartType: {
      type: String,
      default: 'bar'
    }
  },
  computed: {
    actionsAvailable() {
      return this.actions?.actions?.length
    },
    numberOfUsersThatHaveResponded() {
      return this.tags.participation?.amount_10 || 0
    },
    peopleSelectedAtLeastOneAction() {
      return this.actions.actionStats?.numberOfPeopleThatSelectedAnAction
    },
    totalSelectedActions() {
      return (this.actions.actions || []).reduce((sum, a) => {
        sum += a.numberOfPeople
        return sum
      }, 0)
    },
    individualActionsWithLabels() {
      const actionsWithLabels = Object.keys(
        this.actions.summaryActions || {}
      ).reduce((actions, summaryName) => {
        const summary = this.actions.summaryActions[summaryName]
        const summaryActionSelectionCount = summary.reduce((sum, a) => {
          sum += a.numberOfPeople
          return sum
        }, 0)
        actions[summaryName] = {
          summaryActions: summary,
          summaryActionSelectionCount,
          label: this.$tr(
            this.data.summaryActions[summaryName].messages,
            'totalActionsSelectedLabel'
          )
        }
        return actions
      }, {})
      const actionStatsWithLabels = (
        Object.keys(this.actions.summaryActions || {}) || []
      ).reduce((actions, summaryName) => {
        const summary = this.actions.summaryActionStats[summaryName]
        if (summary) {
          const numberOfPeopleThatSelectedAnAction =
            summary.numberOfPeopleThatSelectedAnAction
          actions[summaryName] = {
            numberOfPeopleThatSelectedAnAction,
            percentageOfPeopleThatSelectedAnAction: `(${this.percentageOfSelectedAction(
              numberOfPeopleThatSelectedAnAction
            )})`,
            label: this.$tr(
              this.data.summaryActions[summaryName].messages,
              'peopleSelectedAnActionLabel'
            )
          }
        }
        return actions
      }, {})
      return {
        actionsWithLabels,
        actionStatsWithLabels
      }
    },
    enoughResponses() {
      return this.actionStats?.actions?.length > 0 && !this.isIndividualGroup
    },
    widgetTitle() {
      return this.$t('widgetActionsSummary.title')
    }
  },
  mounted() {
    if (!this.actionsAvailable) {
      this.deregisterWidget({
        id: this.id,
        info: {
          reason: errorTypes.NOT_ENOUGH_DATA,
          hideWidget: true,
          hideWidgetWhenNoSurveyResults: true
        },
        groupName: this.selectedGroupName
      })
    }
  },
  methods: {
    percentageOfSelectedAction(numberOfSelectedAction) {
      return (
        this.$localeNumber(
          (numberOfSelectedAction / this.numberOfUsersThatHaveResponded) * 100,
          0
        ) + ' %'
      )
    }
  }
}
export default ActionSummaryInfoWidget
</script>
